import React, { useState } from "react";
import { useQuery } from "react-query";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "../utils/axios";
import toast from "react-hot-toast";

// Store for cart management
import { useCartStore } from "../store/cartStore";

// Auth context and utilities
import { useAuth } from "../contexts/AuthContext";
import {
  canUserPurchase,
  createLoginRedirectUrl,
  getRoleName,
} from "../utils/authUtils";

// Components
import PurchaseProtection from "../components/PurchaseProtection";

const ProductDetailsPage = () => {
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const { addToCart, cart } = useCartStore();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Default placeholder image
  const defaultProductImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext fill='%236b7280' font-family='Arial, sans-serif' font-size='18' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EProduct Image%3C/text%3E%3C/svg%3E";

  // Fetch product details
  const {
    data: productData,
    isLoading,
    error,
  } = useQuery(
    ["product", id],
    async () => {
      const response = await axios.get(`/api/products/${id}`);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    }
  );

  // Fetch related products
  const { data: relatedProductsData } = useQuery(
    ["relatedProducts", productData?.product?.category],
    async () => {
      if (!productData?.product?.category) return { products: [] };
      const response = await axios.get(
        `/api/products?category=${productData.product.category}&limit=4`
      );
      return response.data;
    },
    {
      enabled: !!productData?.product?.category,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch product reviews
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery(
    ["productReviews", id, reviewsPage],
    async () => {
      const response = await axios.get(
        `/api/reviews/product/${id}?page=${reviewsPage}&limit=5`
      );
      return response.data;
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  );

  const product = productData?.product;
  const relatedProducts =
    relatedProductsData?.products?.filter((p) => p.id !== parseInt(id)) || [];

  // Mock images array - in real app, you'd have multiple images
  const productImages = product?.image_url
    ? [product.image_url, product.image_url, product.image_url]
    : [defaultProductImage];

  const handleAddToCart = (productToAdd, qty = quantity) => {
    if (isAuthenticated && !canUserPurchase(user)) {
      const userRole = getRoleName(user.role);
      toast.error(
        `${userRole} accounts cannot make purchases. Only customer accounts can add items to cart.`
      );
      return;
    }

    for (let i = 0; i < qty; i++) {
      addToCart(productToAdd);
    }
    toast.success(`${qty} x ${productToAdd.name} added to cart!`);
  };

  const handleExternalPayment = (productToBuy) => {
    if (!isAuthenticated) {
      toast.error(`Please login to purchase ${productToBuy.name}`);
      const loginUrl = createLoginRedirectUrl(
        location.pathname + location.search
      );
      navigate(loginUrl);
      return;
    }

    if (!canUserPurchase(user)) {
      const userRole = getRoleName(user.role);
      toast.error(
        `${userRole} accounts cannot make purchases. Only customer accounts can purchase products.`
      );
      return;
    }

    window.open(productToBuy.payment_link, "_blank");
    toast.success("Redirecting to payment gateway...");
  };

  const getCartItemQuantity = (productId) => {
    const item = cart.find((item) => item.id === productId);
    return item ? item.quantity : 0;
  };

  const incrementQuantity = () => {
    if (product.stock_quantity && quantity >= product.stock_quantity) {
      toast.error("Maximum stock limit reached");
      return;
    }
    setQuantity(quantity + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-fuchsia-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Product Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/products"
            className="inline-block px-6 py-3 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 transition-colors"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-fuchsia-600"
              >
                Home
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-3 h-3 text-gray-400 mx-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <Link
                  to="/products"
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-fuchsia-600"
                >
                  Products
                </Link>
              </div>
            </li>
            {product.category && (
              <li>
                <div className="flex items-center">
                  <svg
                    className="w-3 h-3 text-gray-400 mx-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="ml-1 text-sm font-medium text-gray-500">
                    {product.category}
                  </span>
                </div>
              </li>
            )}
            <li>
              <div className="flex items-center">
                <svg
                  className="w-3 h-3 text-gray-400 mx-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ml-1 text-sm font-medium text-gray-500">
                  {product.name}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Main Product Section */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={productImages[selectedImage] || defaultProductImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = defaultProductImage;
                  }}
                />
              </div>

              {/* Thumbnail Images */}
              <div className="grid grid-cols-4 gap-4">
                {productImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index
                        ? "border-fuchsia-600"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = defaultProductImage;
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Category & Stock Badge */}
              <div className="flex items-center gap-3">
                {product.category && (
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">
                    {product.category}
                  </span>
                )}
                {product.stock_quantity !== undefined && (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold ${
                      product.stock_quantity > 0
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {product.stock_quantity > 0
                      ? `${product.stock_quantity} in stock`
                      : "Out of stock"}
                  </span>
                )}
              </div>

              {/* Product Name */}
              <h1 className="text-3xl font-bold text-gray-900">
                {product.name}
              </h1>

              {/* Rating */}
              {product.total_reviews > 0 ? (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(parseFloat(product.average_rating))
                            ? "text-yellow-400 fill-current"
                            : "text-gray-300"
                        }`}
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-gray-600 text-sm">
                    ({product.average_rating} out of 5)
                  </span>
                  <span className="text-gray-400">|</span>
                  <a
                    href="#reviews"
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    {product.total_reviews}{" "}
                    {product.total_reviews === 1 ? "review" : "reviews"}
                  </a>
                </div>
              ) : (
                <div className="text-gray-500 text-sm">No reviews yet</div>
              )}

              {/* Price */}
              <div className="text-4xl font-bold text-gray-900">
                ₹{product.price.toLocaleString()}
              </div>

              {/* Description */}
              <div className="border-t border-b border-gray-200 py-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  Description
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {product.description ||
                    "Professional solution designed to meet your business needs with exceptional quality and results. Our product delivers outstanding performance and reliability."}
                </p>
              </div>

              {/* Quantity Selector - Hidden for now, uncomment to enable */}
              {/* <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-900">
                  Quantity
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    </button>
                    <span className="px-6 py-2 text-gray-900 font-semibold border-x border-gray-300">
                      {quantity}
                    </span>
                    <button
                      onClick={incrementQuantity}
                      disabled={
                        product.stock_quantity &&
                        quantity >= product.stock_quantity
                      }
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                  {getCartItemQuantity(product.id) > 0 && (
                    <span className="text-sm text-gray-600">
                      ({getCartItemQuantity(product.id)} in cart)
                    </span>
                  )}
                </div>
              </div> */}

              {/* Action Buttons */}
              <div className=" flex">
                {product.payment_link ? (
                  <>
                    <PurchaseProtection
                      onPurchaseAction={() => handleExternalPayment(product)}
                      productName={product.name}
                      fallbackText="Buy Now"
                    >
                      <button
                        onClick={() => handleExternalPayment(product)}
                        className="w-full bg-green-600 text-white mr-2 px-6 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors duration-200 shadow-lg"
                      >
                        Buy Now
                      </button>
                    </PurchaseProtection>

                    <button
                      onClick={() => handleAddToCart(product, 1)}
                      disabled={product.stock_quantity === 0}
                      className={`mr-2 w-full px-6 rounded-lg text-md transition-colors duration-200 shadow-lg ${
                        product.stock_quantity === 0
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                      }`}
                    >
                      {product.stock_quantity === 0
                        ? "Out of Stock"
                        : "Add to Cart"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleAddToCart(product, 1)}
                    disabled={product.stock_quantity === 0}
                    className={`w-full py-4 px-6 rounded-lg text-md transition-colors duration-200 shadow-lg ${
                      product.stock_quantity === 0
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                    }`}
                  >
                    {product.stock_quantity === 0
                      ? "Out of Stock"
                      : "Add to Cart"}
                  </button>
                )}

                <Link
                  to="/appointments"
                  className="w-full bg-white px-6 rounded-lg text-center block border-2 border-fuchsia-600 text-fuchsia-600 hover:bg-fuchsia-50 transition-colors duration-200"
                >
                  Get Expert Consultation
                </Link>
              </div>

              {/* Additional Info */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 text-sm">
                    Free shipping on orders over ₹1000
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 text-sm">
                    Easy returns within 30 days
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 text-sm">
                    Secure payment processing
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Features */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Key Features
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-fuchsia-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Premium Quality
                </h3>
                <p className="text-gray-600 text-sm">
                  Crafted with the finest materials and attention to detail
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-fuchsia-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Expert Support
                </h3>
                <p className="text-gray-600 text-sm">
                  24/7 customer support from our dedicated team
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-fuchsia-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Fast Delivery
                </h3>
                <p className="text-gray-600 text-sm">
                  Quick and reliable shipping to your doorstep
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-fuchsia-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Money-Back Guarantee
                </h3>
                <p className="text-gray-600 text-sm">
                  100% satisfaction guaranteed or your money back
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div id="reviews" className="bg-white rounded-2xl shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Customer Reviews
          </h2>

          {reviewsData?.reviews && reviewsData.reviews.length > 0 ? (
            <div className="space-y-6">
              {/* Rating Summary */}
              {reviewsData.statistics && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Average Rating */}
                    <div className="text-center">
                      <div className="text-5xl font-bold text-gray-900 mb-2">
                        {parseFloat(
                          reviewsData.statistics.average_rating
                        ).toFixed(1)}
                      </div>
                      <div className="flex items-center justify-center mb-2">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-6 h-6 ${
                              i <
                              Math.round(
                                parseFloat(
                                  reviewsData.statistics.average_rating
                                )
                              )
                                ? "text-yellow-400 fill-current"
                                : "text-gray-300"
                            }`}
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-gray-600">
                        Based on {reviewsData.statistics.total_reviews}{" "}
                        {reviewsData.statistics.total_reviews === 1
                          ? "review"
                          : "reviews"}
                      </p>
                    </div>

                    {/* Rating Breakdown */}
                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const count =
                          reviewsData.statistics[
                            `${
                              star === 5
                                ? "five"
                                : star === 4
                                ? "four"
                                : star === 3
                                ? "three"
                                : star === 2
                                ? "two"
                                : "one"
                            }_star`
                          ] || 0;
                        const percentage =
                          reviewsData.statistics.total_reviews > 0
                            ? (count / reviewsData.statistics.total_reviews) *
                              100
                            : 0;

                        return (
                          <div
                            key={star}
                            className="flex items-center space-x-3"
                          >
                            <span className="text-sm font-medium text-gray-700 w-12">
                              {star} star
                            </span>
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-yellow-400 h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-12 text-right">
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Individual Reviews */}
              <div className="space-y-4">
                {reviewsLoading ? (
                  <div className="text-center py-8">
                    <div className="loading-dots mx-auto">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                ) : (
                  reviewsData.reviews.map((review) => (
                    <div
                      key={review.id}
                      className="border-b border-gray-200 pb-4 last:border-b-0"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-semibold text-gray-900">
                              {review.user_name}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="text-sm text-gray-600">
                              {new Date(review.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )}
                            </span>
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? "text-yellow-400 fill-current"
                                    : "text-gray-300"
                                }`}
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                        {review.updated_at !== review.created_at && (
                          <span className="text-xs text-gray-500 italic">
                            Edited
                          </span>
                        )}
                      </div>
                      {review.review_text && (
                        <p className="text-gray-700 leading-relaxed">
                          {review.review_text}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {reviewsData.pagination &&
                reviewsData.pagination.totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-4 pt-4">
                    <button
                      onClick={() => setReviewsPage((p) => Math.max(1, p - 1))}
                      disabled={reviewsPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {reviewsPage} of {reviewsData.pagination.totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setReviewsPage((p) =>
                          Math.min(reviewsData.pagination.totalPages, p + 1)
                        )
                      }
                      disabled={
                        reviewsPage === reviewsData.pagination.totalPages
                      }
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Reviews Yet
              </h3>
              <p className="text-gray-600">
                Be the first to review this product!
              </p>
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Related Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.id}`}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-200"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={relatedProduct.image_url || defaultProductImage}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = defaultProductImage;
                      }}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-fuchsia-600 transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <div className="text-lg font-bold text-gray-900">
                      ₹{relatedProduct.price.toLocaleString()}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsPage;
